import React, { useCallback, useEffect, useState } from 'react'
import { Library, Loader2, AlertCircle, Trash2, Pencil, Plus, X, Eye, EyeOff, Languages } from 'lucide-react'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { TranslationEditorModal, type TranslationFieldDef } from '@/components/admin/TranslationEditorModal'
import { type TranslationRow } from '@/services/translationService'
import {
  listResourcesAdmin,
  getResourceStats,
  createResource,
  updateResource,
  changeResourceStatus,
  deleteResource,
  RESOURCE_TYPE_LABELS,
  AUDIENCE_LABELS,
  LANGUAGE_LABELS,
  RESOURCE_STATUS_LABELS,
  type Resource,
  type ResourceRequest,
  type ResourceType,
  type ResourceAudience,
  type ResourceStatus,
} from '@/services/resourceService'

const STATUS_FILTERS: (ResourceStatus | 'ALL')[] = ['ALL', 'DRAFT', 'PUBLISHED', 'ARCHIVED']
const TYPES = Object.keys(RESOURCE_TYPE_LABELS) as ResourceType[]
const AUDIENCES = Object.keys(AUDIENCE_LABELS) as ResourceAudience[]
const LANGS = Object.keys(LANGUAGE_LABELS)
const STATUS_LIST: ResourceStatus[] = ['DRAFT', 'PUBLISHED', 'ARCHIVED']

const statusTone: Record<ResourceStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PUBLISHED: 'bg-green-100 text-green-700',
  ARCHIVED: 'bg-amber-100 text-amber-700',
}

const EMPTY_FORM: ResourceRequest = {
  title: '', slug: '', description: '', resourceType: 'GUIDELINE',
  audience: 'PATIENT', language: 'en', sourceOrg: '', fileUrl: '', externalUrl: '',
  fileType: '', fileSize: '', thumbnailUrl: '', isFeatured: false, status: 'DRAFT',
}

const ResourcesManagement = React.memo(() => {
  const [items, setItems] = useState<Resource[]>([])
  const [stats, setStats] = useState<{ TOTAL: number; DRAFT: number; PUBLISHED: number; ARCHIVED: number }>({ TOTAL: 0, DRAFT: 0, PUBLISHED: 0, ARCHIVED: 0 })
  const [statusFilter, setStatusFilter] = useState<ResourceStatus | 'ALL'>('ALL')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actingId, setActingId] = useState<number | null>(null)
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; msg: string } | null>(null)
  const [editing, setEditing] = useState<Resource | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<ResourceRequest>(EMPTY_FORM)
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [translateId, setTranslateId] = useState<number | null>(null)
  const size = 20

  const TRANSLATE_FIELDS: TranslationFieldDef[] = [
    { key: 'title', label: 'Title', type: 'text', required: true },
    { key: 'description', label: 'Description', type: 'textarea', rows: 3 },
    { key: 'sourceOrg', label: 'Source / publisher', type: 'text' },
  ]

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const [data, s] = await Promise.all([
        listResourcesAdmin({ status: statusFilter === 'ALL' ? undefined : statusFilter, q: q || undefined, page, size }),
        getResourceStats(),
      ])
      setItems(data.content); setTotalPages(data.totalPages); setStats(s)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load resources.')
    } finally { setLoading(false) }
  }, [statusFilter, q, page])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(0) }, [statusFilter, q])

  const flash = (tone: 'success' | 'error', msg: string) => { setToast({ tone, msg }); setTimeout(() => setToast(null), 3000) }

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setFormError(null); setShowForm(true) }
  const openEdit = (r: Resource) => {
    setEditing(r)
    setForm({
      title: r.title, slug: r.slug, description: r.description ?? '', resourceType: r.resourceType,
      audience: r.audience, language: r.language, sourceOrg: r.sourceOrg ?? '', fileUrl: r.fileUrl ?? '',
      externalUrl: r.externalUrl ?? '', fileType: r.fileType ?? '', fileSize: r.fileSize ?? '',
      thumbnailUrl: r.thumbnailUrl ?? '', isFeatured: r.isFeatured, status: r.status,
    })
    setFormError(null); setShowForm(true)
  }

  const closeForm = () => { setShowForm(false); setEditing(null); setForm(EMPTY_FORM); setFormError(null) }

  const save = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!form.title.trim()) { setFormError('Title is required.'); return }
    if (!form.resourceType) { setFormError('Resource type is required.'); return }
    setSaving(true); setFormError(null)
    try {
      const payload: ResourceRequest = { ...form, title: form.title.trim(), isFeatured: Boolean(form.isFeatured) }
      if (editing) {
        await updateResource(editing.id, payload)
        flash('success', `Updated “${form.title}”`)
      } else {
        await createResource(payload)
        flash('success', `Created “${form.title}”`)
      }
      closeForm(); await load()
    } catch (e: any) {
      setFormError(e?.response?.data?.message || 'Failed to save.')
    } finally { setSaving(false) }
  }

  const changeStatus = async (r: Resource, status: ResourceStatus) => {
    setActingId(r.id)
    try { await changeResourceStatus(r.id, status); flash('success', `“${r.title}” → ${RESOURCE_STATUS_LABELS[status]}`); await load() }
    catch (e: any) { flash('error', e?.response?.data?.message || 'Failed.') }
    finally { setActingId(null) }
  }

  const remove = async (r: Resource) => {
    if (!window.confirm(`Delete “${r.title}”? This cannot be undone.`)) return
    setActingId(r.id)
    try { await deleteResource(r.id); flash('success', 'Resource deleted'); await load() }
    catch (e: any) { flash('error', e?.response?.data?.message || 'Failed.') }
    finally { setActingId(null) }
  }

  return (
    <GovLayout showSidebar={false}>
      <div className="space-y-6">
        <GovPageTitle title="Resource Library" subtitle="Add and manage guidelines, reports, IEC material, videos and publications" icon={Library} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <GovCard className="text-center"><p className="text-2xl font-bold text-gray-900">{stats.TOTAL}</p><p className="text-sm text-gray-500">Total</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-gray-500">{stats.DRAFT}</p><p className="text-sm text-gray-500">Draft</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-green-600">{stats.PUBLISHED}</p><p className="text-sm text-gray-500">Published</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-amber-600">{stats.ARCHIVED}</p><p className="text-sm text-gray-500">Archived</p></GovCard>
        </div>

        <GovCard>
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_FILTERS.map((f) => (
              <button key={f} onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {f === 'ALL' ? 'All' : RESOURCE_STATUS_LABELS[f]}
              </button>
            ))}
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title/description…" className="text-sm border border-gray-200 rounded-md px-3 py-1.5 ml-auto" />
            <GovButton variant="primary" size="sm" onClick={openCreate} icon={<Plus className="w-4 h-4" />}>Add resource</GovButton>
          </div>
        </GovCard>

        {toast && (
          <div role="status" className={`flex items-center gap-2 p-3 rounded-lg text-sm ${toast.tone === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {toast.tone === 'success' ? '✓' : <AlertCircle className="w-4 h-4" />} {toast.msg}
          </div>
        )}

        <GovCard>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-900">Resources</h2>
            <button onClick={load} disabled={loading} className="text-xs text-blue-600 hover:underline disabled:opacity-50">{loading ? 'Loading…' : 'Refresh'}</button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
          ) : error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm"><AlertCircle className="w-4 h-4" /> {error}</div>
          ) : items.length === 0 ? (
            <GovEmptyState icon="📚" title="No resources" description="Add your first resource, or adjust the filter/search." />
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((r) => (
                <div key={r.id} className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusTone[r.status]}`}>{RESOURCE_STATUS_LABELS[r.status]}</span>
                        {r.isFeatured && <span className="text-xs text-teal-600 font-medium">★ Featured</span>}
                        <span className="text-xs text-gray-400">{RESOURCE_TYPE_LABELS[r.resourceType]}</span>
                      </div>
                      <h4 className="font-medium text-gray-900">{r.title}</h4>
                      {r.description && <p className="text-sm text-gray-600 line-clamp-2 mt-1">{r.description}</p>}
                      <p className="text-xs text-gray-400 mt-1">
                        {r.audience && `${AUDIENCE_LABELS[r.audience]}`}
                        {r.language && ` · ${LANGUAGE_LABELS[r.language] ?? r.language}`}
                        {r.sourceOrg && ` · ${r.sourceOrg}`}
                      </p>
                      {(r.fileUrl || r.externalUrl) && (
                        <a href={r.externalUrl || r.fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 inline-block truncate max-w-full">
                          {r.externalUrl || r.fileUrl}
                        </a>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                      {actingId === r.id && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                      {r.status === 'PUBLISHED'
                        ? <GovButton variant="ghost" size="sm" onClick={() => changeStatus(r, 'ARCHIVED')} icon={<EyeOff className="w-3.5 h-3.5" />}>Archive</GovButton>
                        : <GovButton variant="ghost" size="sm" onClick={() => changeStatus(r, 'PUBLISHED')} icon={<Eye className="w-3.5 h-3.5" />}>Publish</GovButton>}
                      <button onClick={() => openEdit(r)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => setTranslateId(r.id)} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md" aria-label="Translate"><Languages className="w-4 h-4" /></button>
                      <button onClick={() => remove(r)} disabled={actingId === r.id} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Previous</GovButton>
              <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</GovButton>
            </div>
          )}
        </GovCard>

        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" onClick={closeForm}>
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{editing ? 'Edit resource' : 'Add resource'}</h3>
                  <p className="text-sm text-gray-500 mt-1">Only add verified resources with real sources. Do not fabricate government documents or data.</p>
                </div>
                <button onClick={closeForm} className="text-gray-400 hover:text-gray-700" aria-label="Close"><X className="w-5 h-5" /></button>
              </div>

              {formError && <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm" role="alert"><AlertCircle className="w-4 h-4" /> {formError}</div>}

              <form onSubmit={save} noValidate className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="r-title">Title <span className="text-red-500">*</span></label>
                  <input id="r-title" type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="r-slug">Slug (optional)</label>
                    <input id="r-slug" type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="input" placeholder="auto from title" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="r-type">Resource type <span className="text-red-500">*</span></label>
                    <select id="r-type" value={form.resourceType} onChange={(e) => setForm({ ...form, resourceType: e.target.value as ResourceType })} className="select">
                      {TYPES.map((t) => <option key={t} value={t}>{RESOURCE_TYPE_LABELS[t]}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="r-aud">Audience</label>
                    <select id="r-aud" value={form.audience ?? ''} onChange={(e) => setForm({ ...form, audience: (e.target.value || undefined) as ResourceAudience | undefined })} className="select">
                      <option value="">—</option>
                      {AUDIENCES.map((a) => <option key={a} value={a}>{AUDIENCE_LABELS[a]}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="r-lang">Language</label>
                    <select id="r-lang" value={form.language ?? ''} onChange={(e) => setForm({ ...form, language: e.target.value || undefined })} className="select">
                      <option value="">—</option>
                      {LANGS.map((l) => <option key={l} value={l}>{LANGUAGE_LABELS[l]}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="r-status">Status</label>
                    <select id="r-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as ResourceStatus })} className="select">
                      {STATUS_LIST.map((s) => <option key={s} value={s}>{RESOURCE_STATUS_LABELS[s]}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="r-desc">Description</label>
                  <textarea id="r-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="r-src">Source / publisher</label>
                    <input id="r-src" type="text" value={form.sourceOrg} onChange={(e) => setForm({ ...form, sourceOrg: e.target.value })} className="input" placeholder="e.g. Central TB Division, MoHFW" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="r-ext">External URL (link)</label>
                    <input id="r-ext" type="url" value={form.externalUrl} onChange={(e) => setForm({ ...form, externalUrl: e.target.value })} className="input" placeholder="https://…" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="r-file">File URL (download)</label>
                    <input id="r-file" type="text" value={form.fileUrl} onChange={(e) => setForm({ ...form, fileUrl: e.target.value })} className="input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="r-ft">File type</label>
                    <input id="r-ft" type="text" value={form.fileType} onChange={(e) => setForm({ ...form, fileType: e.target.value })} className="input" placeholder="PDF, MP4…" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="r-fs">File size</label>
                    <input id="r-fs" type="text" value={form.fileSize} onChange={(e) => setForm({ ...form, fileSize: e.target.value })} className="input" placeholder="2.4 MB" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={Boolean(form.isFeatured)} onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })} />
                  Featured resource
                </label>

                <div className="flex items-center gap-3 pt-2">
                  <GovButton type="submit" variant="primary" disabled={saving} icon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}>{saving ? 'Saving…' : 'Save resource'}</GovButton>
                  <GovButton variant="ghost" onClick={closeForm}>Cancel</GovButton>
                </div>
              </form>
            </div>
          </div>
        )}

        <TranslationEditorModal
          open={translateId !== null}
          basePath="/resources"
          id={translateId}
          itemLabel="resource"
          fields={TRANSLATE_FIELDS}
          canonical={translateId !== null ? (items.find((r) => r.id === translateId) as unknown as Partial<TranslationRow> | undefined) : undefined}
          onClose={() => setTranslateId(null)}
        />
      </div>
    </GovLayout>
  )
})

ResourcesManagement.displayName = 'ResourcesManagement'
export default ResourcesManagement